import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { auth, db, functionsInstance } from '../../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import RatingDisplay from '../../components/worker/RatingDisplay';
import WorkerBottomNav from '../../components/worker/WorkerBottomNav';
import { useLanguage } from '../../context/LanguageContext';
import { EMPTY_BANK_ACCOUNT, maskAccountNumber, normalizeBankAccount, validateBankAccount } from '../../utils/bankDetails';
import '../../styles/worker-dashboard.css';

export default function WorkerProfile() {
  const [worker, setWorker] = useState(null);
  const [loading, setLoading] = useState(true);
  const [payoutBankAccount, setPayoutBankAccount] = useState(EMPTY_BANK_ACCOUNT);
  const [bankSaving, setBankSaving] = useState(false);
  const [bankMessage, setBankMessage] = useState('');
  const [shareMessage, setShareMessage] = useState('');
  
  const { language, setLanguage, supportedLanguages } = useLanguage();

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      if (!u) { setLoading(false); return; }
      try {
        const snap = await getDoc(doc(db, 'worker_auth', u.uid));
        if (snap.exists()) {
          const data = snap.data();
          setWorker({ ...data, uid: u.uid });
          setPayoutBankAccount(data.payoutBankAccount || EMPTY_BANK_ACCOUNT);
        }
      } catch {}
      finally { setLoading(false); }
    });
    return () => unsub();
  }, []);

  const recentReviews = Array.isArray(worker?.recentReviews) ? worker.recentReviews : [];
  const rating = Number(worker?.rating || worker?.avgRating || 0);
  const totalReviews = Number(worker?.reviewCount || recentReviews.length || 0);
  const storedBreakdown = worker?.ratingBreakdown || {};
  const ratingBreakdown = [5, 4, 3, 2, 1].map(star => ({
    star,
    count: Number(storedBreakdown[star] ?? recentReviews.filter(r => Number(r.rating) === star).length),
    pct: totalReviews > 0
      ? Math.round((Number(storedBreakdown[star] ?? recentReviews.filter(r => Number(r.rating) === star).length) / totalReviews) * 100)
      : 0
  }));

  const initials = (worker?.name || 'W').split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  const referralCode = worker?.referralPublicCode || worker?.referralCode || '';
  const referralLink = referralCode ? `https://gigto.in/#/auth?phase=signup&mode=worker&ref=${encodeURIComponent(referralCode)}` : '';
  const referralWhatsappLink = referralLink
    ? `https://wa.me/?text=${encodeURIComponent(`Join Gigto as a worker with my referral link: ${referralLink}`)}`
    : '';

  const savePayoutBankAccount = async () => {
    setBankMessage('');
    const account = normalizeBankAccount(payoutBankAccount);
    const validationError = validateBankAccount(account);
    if (validationError) {
      setBankMessage(validationError);
      return;
    }
    const uid = auth.currentUser?.uid || worker?.uid;
    if (!uid) {
      setBankMessage('Sign in again to save payout account.');
      return;
    }

    setBankSaving(true);
    try {
      const result = await httpsCallable(functionsInstance, 'updateWorkerPayoutAccount')({ account });
      const masked = result?.data?.masked || {};
      setWorker(prev => ({ ...(prev || {}), payoutBankAccount: null, payoutBankAccountMasked: masked }));
      setPayoutBankAccount({ ...EMPTY_BANK_ACCOUNT, accountHolderName: account.accountHolderName, bankName: account.bankName });
      setBankMessage('Payout account saved.');
    } catch (err) {
      setBankMessage(err.message || 'Failed to save payout account.');
    } finally {
      setBankSaving(false);
    }
  };

  const copyReferralText = async (value, successText) => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setShareMessage(successText);
    } catch {
      setShareMessage('Could not copy right now.');
    }
  };

  if (loading) {
    return (
      <div className="worker-page">
        <div className="worker-container">
          <div className="skeleton" style={{ height: 200, borderRadius: 14, marginBottom: 14 }} />
          <div className="skeleton" style={{ height: 120, borderRadius: 14, marginBottom: 14 }} />
        </div>
      </div>
    );
  }

  return (
    <div className="worker-page">
      <div className="worker-container">
        <div className="worker-page-topbar">
          <Link to="/worker/dashboard" className="worker-back-link" aria-label="Back to worker dashboard">←</Link>
          <h2 className="worker-page-title">My Profile</h2>
        </div>

        {/* Profile Header */}
        <div className="worker-card" style={{ textAlign: 'center', paddingTop: 24, paddingBottom: 24 }}>
          <div style={{
            width: 80, height: 80, borderRadius: '50%',
            background: 'linear-gradient(135deg, #7C3AED, #A259FF)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 32, fontWeight: 700, color: 'white',
            margin: '0 auto 12px'
          }}>
            {initials}
          </div>
          <h3 style={{ margin: '0 0 4px', fontSize: 20, color: '#1F1144' }}>{worker?.name || 'Worker'}</h3>
          <div style={{ color: '#6B7280', fontSize: 14, marginBottom: 8 }}>
            {(worker?.gigTypes && worker.gigTypes.length > 0)
              ? worker.gigTypes.map(t => t.charAt(0).toUpperCase() + t.slice(1).replace(/-/g, ' ')).join(', ')
              : (worker?.gigType || 'General Worker')
            }
          </div>
          <RatingDisplay rating={rating} size="md" />
          <div style={{ color: '#6B7280', fontSize: 13, marginTop: 4 }}>
            {totalReviews > 0 ? `(${totalReviews} reviews)` : 'No reviews yet'}
          </div>
          <Link
            to="/profile"
            style={{
              display: 'inline-block',
              marginTop: 14,
              padding: '8px 20px',
              background: '#F5F3FF',
              color: '#7C3AED',
              borderRadius: 10,
              fontSize: 14,
              fontWeight: 600,
              textDecoration: 'none',
              border: '1px solid #E9D5FF'
            }}
          >
            ✏️ Edit Profile
          </Link>
        </div>

        {/* Info */}
        <div className="worker-card">
          <h4 style={{ margin: '0 0 12px', color: '#1F1144' }}>📋 Details</h4>
          {[
            { label: 'Phone', value: worker?.phone },
            { label: 'Email', value: worker?.email },
            { label: 'Area', value: worker?.area },
            { label: 'Status', value: worker?.approvalStatus },
            { label: 'Member Since', value: worker?.createdAt ? new Date(worker.createdAt).getFullYear() : '2026' },
          ].map(row => row.value ? (
            <div key={row.label} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #F3F4F6', fontSize: 14 }}>
              <span style={{ color: '#6B7280' }}>{row.label}</span>
              <span style={{ fontWeight: 600, color: '#1F1144' }}>{row.value}</span>
            </div>
          ) : null)}
          {(worker?.gigTypes?.length > 0 || worker?.gigType) && (
            <div style={{ marginTop: 10 }}>
              <div style={{ color: '#6B7280', fontSize: 13, marginBottom: 6 }}>Skills/Job Types</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {(worker.gigTypes?.length > 0 ? worker.gigTypes : [worker.gigType]).filter(Boolean).map(t => (
                  <span key={t} style={{ background: '#EDE9FE', color: '#7C3AED', padding: '4px 10px', borderRadius: 20, fontSize: 13, fontWeight: 600 }}>
                    {t.charAt(0).toUpperCase() + t.slice(1).replace(/-/g, ' ')}
                  </span>
                ))}
              </div>

            </div>
          )}
        </div>

        <div className="worker-card">
          <h4 style={{ margin: '0 0 12px', color: '#1F1144' }}>🌐 App Language</h4>
          <p style={{ margin: '0 0 12px', color: '#6B7280', fontSize: 13 }}>
            Choose the language for your worker dashboard.
          </p>
          <select 
            value={language}
            onChange={(e) => setLanguage(e.target.value)}
            style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #E5E7EB', fontSize: 14 }}
          >
            {supportedLanguages.map(l => (
              <option key={l.code} value={l.code}>{l.nativeLabel} ({l.label})</option>
            ))}
          </select>
        </div>

        <div className="worker-card">
          <h4 style={{ margin: '0 0 12px', color: '#1F1144' }}>Referral</h4>
          <p style={{ margin: '0 0 12px', color: '#6B7280', fontSize: 13 }}>
            Each referred worker who completes one eligible job adds 10 free days to your access, up to the platform cap.
          </p>
          <div style={{ display: 'grid', gap: 10 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '12px 14px', borderRadius: 12, background: '#F8FAFC', border: '1px solid #E5E7EB', alignItems: 'center', flexWrap: 'wrap' }}>
              <div>
                <div style={{ color: '#6B7280', fontSize: 12, marginBottom: 4 }}>Referral code</div>
                <div style={{ fontWeight: 800, color: '#1F1144', fontSize: 16 }}>{referralCode || 'Available after profile sync'}</div>
              </div>
              <button
                type="button"
                className="btn-primary"
                onClick={() => copyReferralText(referralCode, 'Referral code copied.')}
                disabled={!referralCode}
                style={{ padding: '10px 14px' }}
              >
                Copy code
              </button>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '12px 14px', borderRadius: 12, background: '#F8FAFC', border: '1px solid #E5E7EB', alignItems: 'center', flexWrap: 'wrap' }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ color: '#6B7280', fontSize: 12, marginBottom: 4 }}>Referral signup link</div>
                <div style={{ fontWeight: 600, color: '#1F1144', fontSize: 13, wordBreak: 'break-all' }}>{referralLink || 'Available after profile sync'}</div>
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button
                  type="button"
                  className="btn-primary"
                  onClick={() => copyReferralText(referralLink, 'Referral link copied.')}
                  disabled={!referralLink}
                  style={{ padding: '10px 14px' }}
                >
                  Copy link
                </button>
                {referralWhatsappLink && (
                  <a
                    href={referralWhatsappLink}
                    target="_blank"
                    rel="noreferrer"
                    className="btn-primary"
                    style={{ padding: '10px 14px', textDecoration: 'none' }}
                  >
                    Share on WhatsApp
                  </a>
                )}
              </div>
            </div>
            {shareMessage && (
              <div style={{ color: shareMessage.includes('copied') ? '#059669' : '#B91C1C', fontSize: 13, fontWeight: 700 }}>
                {shareMessage}
              </div>
            )}
          </div>
        </div>

        <div className="worker-card">
          <h4 style={{ margin: '0 0 12px', color: '#1F1144' }}>Payout Bank Account</h4>
          <p style={{ margin: '0 0 12px', color: '#6B7280', fontSize: 13 }}>
            Gigtos uses this account to pay completed worker earnings after consumer payment confirmation.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10 }}>
            <input
              placeholder="Account holder name"
              value={payoutBankAccount.accountHolderName}
              onChange={e => setPayoutBankAccount({ ...payoutBankAccount, accountHolderName: e.target.value })}
              style={{ padding: 10, borderRadius: 8, border: '1px solid #E5E7EB' }}
            />
            <input
              placeholder="Bank name"
              value={payoutBankAccount.bankName}
              onChange={e => setPayoutBankAccount({ ...payoutBankAccount, bankName: e.target.value })}
              style={{ padding: 10, borderRadius: 8, border: '1px solid #E5E7EB' }}
            />
            <input
              placeholder="Account number"
              inputMode="numeric"
              value={payoutBankAccount.accountNumber}
              onChange={e => setPayoutBankAccount({ ...payoutBankAccount, accountNumber: e.target.value })}
              style={{ padding: 10, borderRadius: 8, border: '1px solid #E5E7EB' }}
            />
            <input
              placeholder="IFSC"
              value={payoutBankAccount.ifsc}
              onChange={e => setPayoutBankAccount({ ...payoutBankAccount, ifsc: e.target.value.toUpperCase() })}
              style={{ padding: 10, borderRadius: 8, border: '1px solid #E5E7EB' }}
            />
            <input
              placeholder="UPI ID optional"
              value={payoutBankAccount.upiId}
              onChange={e => setPayoutBankAccount({ ...payoutBankAccount, upiId: e.target.value })}
              style={{ padding: 10, borderRadius: 8, border: '1px solid #E5E7EB' }}
            />
          </div>
          <div style={{ marginTop: 10, display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ color: '#6B7280', fontSize: 13 }}>
              Saved account: {worker?.payoutBankAccountMasked?.accountNumberMasked || maskAccountNumber(worker?.payoutBankAccount?.accountNumber)}
            </span>
            <button
              type="button"
              className="btn-primary"
              onClick={savePayoutBankAccount}
              disabled={bankSaving}
              style={{ padding: '10px 16px' }}
            >
              {bankSaving ? 'Saving...' : 'Save payout account'}
            </button>
          </div>
          {bankMessage && (
            <div style={{ marginTop: 10, color: bankMessage.includes('saved') ? '#059669' : '#b91c1c', fontWeight: 700, fontSize: 13 }}>
              {bankMessage}
            </div>
          )}
        </div>

        {/* Stats */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 14 }}>
          {[
            { label: 'Completed', value: worker?.completedJobs || 0, icon: '✅' },
            { label: 'Active Hours\nThis Week', value: '24h', icon: '⏱️' },
            { label: 'Rating', value: `${rating}★`, icon: '⭐' },
          ].map(s => (
            <div key={s.label} className="stat-card">
              <span style={{ fontSize: 18 }}>{s.icon}</span>
              <span className="stat-value" style={{ fontSize: 18 }}>{s.value}</span>
              <span className="stat-label" style={{ whiteSpace: 'pre-line' }}>{s.label}</span>
            </div>
          ))}
        </div>

        {/* Rating Breakdown */}
        <div className="worker-card">
          <h4 style={{ margin: '0 0 12px', color: '#1F1144' }}>⭐ Rating Breakdown</h4>
          {ratingBreakdown.map(({ star, count, pct }) => (
            <div key={star} className="rating-bar-row">
              <span style={{ fontSize: 13, color: '#6B7280', width: 20 }}>{star}★</span>
              <div className="rating-bar-track">
                <div className="rating-bar-fill" style={{ width: `${pct}%` }} />
              </div>
              <span style={{ fontSize: 13, color: '#6B7280', width: 24, textAlign: 'right' }}>{count}</span>
            </div>
          ))}
        </div>

        {/* Recent Reviews */}
        <h3 className="section-title">💬 Recent Reviews</h3>
        {recentReviews.length > 0 ? (
          recentReviews.map(review => (
            <div key={review.id || `${review.reviewer}_${review.date}`} className="worker-card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
                <div style={{ fontWeight: 600, fontSize: 14, color: '#1F1144' }}>{review.reviewer || 'Consumer'}</div>
                <RatingDisplay rating={Number(review.rating || 0)} size="sm" showNumber={false} />
              </div>
              {review.comment && <div style={{ fontSize: 14, color: '#6B7280', fontStyle: 'italic' }}>"{review.comment}"</div>}
              <div style={{ fontSize: 12, color: '#9CA3AF', marginTop: 4 }}>{review.date || 'Recent'}</div>
            </div>
          ))
        ) : (
          <div className="empty-state">
            <div className="empty-icon">Reviews</div>
            <h3>No reviews yet</h3>
            <p>Customer reviews will appear after completed jobs.</p>
          </div>
        )}
      </div>
      <WorkerBottomNav />
    </div>
  );
}
